import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import {
  Action,
  Alert,
  Card,
  Page,
  EmailInput,
  TextInput,
} from 'corso-admin-ui';
import { CrewMerchantUi, helperSchema } from 'corso-types';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import api from '~/api';
import { useStoreId } from '~/providers/StoreUserProvider';
import { ErrorResponse } from '~/utils/errorResponse';

const claimCreateOrderLookupSchema = z.object({
  emailOrPostalCode: z.string().email(),
  orderNo: helperSchema.nonEmptyString,
});

export default function OrderLookup({
  onOrderLookup,
}: {
  onOrderLookup?: (order: CrewMerchantUi.CrewOrder) => void;
}) {
  const storeId = useStoreId();

  const {
    isPending,
    mutateAsync,
    isError,
    error: mutationError,
  } = useMutation({
    mutationFn: api.store(storeId).order.get,
  });

  const { register, formState, handleSubmit } = useForm<
    z.infer<typeof claimCreateOrderLookupSchema>
  >({
    resolver: zodResolver(claimCreateOrderLookupSchema),
  });

  return (
    <Page title="Order Lookup" size="narrow">
      <Card>
        <form
          onSubmit={(e) => {
            handleSubmit(
              (fieldValues) => mutateAsync(fieldValues).then(onOrderLookup),
              (fieldErrors) => console.warn(fieldErrors),
            )(e).catch((error) => {
              // no-op on ErrorResponse, because they're handled and displayed elsewhere
              if (error instanceof ErrorResponse) return;
              console.error('Unhandled Order Lookup Error', error);
            });
          }}
        >
          <div className="space-y-4">
            <EmailInput
              id="customer-email"
              label="Email"
              placeholder="john.doe@example.com"
              required
              {...register('emailOrPostalCode')}
              error={formState.errors.emailOrPostalCode?.message}
            />
            <TextInput
              id="order-number"
              label="Order / Registration Number"
              placeholder="#12345 / #REG-12345"
              required
              {...register('orderNo')}
              error={formState.errors.orderNo?.message}
            />
            <Action type="submit" variant="primary" loading={isPending}>
              Lookup Order
            </Action>
            {isError &&
              (mutationError instanceof ErrorResponse ? (
                <Alert variant="critical" heading={mutationError.message} />
              ) : (
                <Alert
                  variant="critical"
                  heading="Something Went Wrong. Please try again."
                />
              ))}
          </div>
          {/* // ? maybe support idFromPlatform lookup too */}
        </form>
      </Card>
    </Page>
  );
}
